import { createBrowserRouter, RouteObject } from 'react-router-dom';
import GeneralError from './pages/errors/general-error';
import MaintenanceError from './pages/errors/maintenance-error';
import NotFoundError from './pages/errors/not-found-error';
import AdminProtectRoute, { SuperAdminProtectRoute } from './pages/admin';
import { Suspense, lazy } from 'react';
import ChunkErrorBoundary from './components/ChunkErrorBoundary';
import { IconLoader } from '@tabler/icons-react';

// Helper function to safely load lazy components with error boundary and suspense
const lazyWithRetry = (importFunc: () => Promise<any>) => {
  const LazyComponent = lazy(async () => {
    try {
      const module = await importFunc();
      return { default: module.default };
    } catch {
      // Wait a bit before retrying to avoid immediate failures
      await new Promise(resolve => setTimeout(resolve, 1000));
      const module = await importFunc();
      return { default: module.default };
    }
  });

  return (
    <ChunkErrorBoundary>
      <Suspense
        fallback={
          <div className="flex items-center justify-center w-full h-[calc(100vh-var(--header-height))]">
            <IconLoader className="w-8 h-8 animate-spin text-brand-600" />
          </div>
        }
      >
        <LazyComponent />
      </Suspense>
    </ChunkErrorBoundary>
  );
};

// Route configurations
const authRoutes: RouteObject[] = [
  {
    path: '/login',
    element: lazyWithRetry(() => import('./pages/auth/login')),
    errorElement: <MaintenanceError />,
  },
  {
    path: '/signup',
    element: lazyWithRetry(() => import('./pages/auth/sign-up')),
    errorElement: <MaintenanceError />,
  },
  {
    path: '/select-workspace',
    element: lazyWithRetry(() => import('./pages/auth/workspace')),
    errorElement: <MaintenanceError />,
  },
  {
    path: '/onboarding',
    element: lazyWithRetry(() => import('./pages/auth/onboarding')),
    errorElement: <MaintenanceError />,
  },
  {
    path: '/forgot-password',
    element: lazyWithRetry(() => import('./pages/auth/forgot-password')),
    errorElement: <MaintenanceError />,
  },
  {
    path: '/reset-password/:code',
    element: lazyWithRetry(() => import('./pages/auth/reset-password')),
    errorElement: <MaintenanceError />,
  },
  {
    path: '/auth/pn/callback',
    element: lazyWithRetry(() => import('./pages/auth/pn-callback')),
    errorElement: <MaintenanceError />,
  },
  {
    path: '/auth/mock-fep',
    element: lazyWithRetry(() => import('./pages/auth/mock-fep')),
    errorElement: <MaintenanceError />,
  },
];

const sharingSessionRoutes: RouteObject[] = [
  {
    path: '/sharing-session',
    element: lazyWithRetry(() => import('./pages/sharing-session')),
  },
  {
    path: '/sharing-session/:id',
    element: lazyWithRetry(() => import('./pages/sharing-session/sharing-detail')),
  },
];

const libraryRoutes: RouteObject[] = [
  {
    path: '/library',
    element: lazyWithRetry(() => import('./pages/library')),
  },
  {
    path: '/library/:id',
    element: lazyWithRetry(() => import('./pages/library/resources')),
  },
  {
    path: '/library/interview-detail/:resource_id',
    element: lazyWithRetry(() => import('./pages/library/resource-detail')),
  },
];

const activeIssueRoutes: RouteObject[] = [
  {
    path: '/active-issue',
    element: lazyWithRetry(() => import('./pages/active-issues')),
  },
  {
    path: '/:agent_id/active-issue/aisc',
    element: lazyWithRetry(() => import('./pages/aisc-demo')),
  },
  {
    path: '/:agent_id/active-issue/:id',
    element: lazyWithRetry(() => import('./pages/active-issues/detail')),
  },
];

const dxaFactoryRoutes: RouteObject[] = [
  {
    path: '/agents',
    element: lazyWithRetry(() => import('./pages/train')),
  },
  {
    path: '/agents/aisc',
    element: lazyWithRetry(() => import('./pages/aisc-demo/latest')),
  },
  {
    path: '/agents/aisc/troubleshooting',
    element: lazyWithRetry(() => import('./pages/aisc-demo/trouble-shooting')),
  },
  {
    path: '/agents/falcon-grow',
    element: lazyWithRetry(() => import('./pages/honeywell')),
  },
  {
    path: '/agents/falcon-lead',
    element: lazyWithRetry(() => import('./pages/honeywell/honeywell-lead')),
  },
  {
    path: '/agents/falcon-operator',
    element: lazyWithRetry(() => import('./pages/honeywell/honeywell-operator')),
  },
  {
    path: '/agents/falcon-network',
    element: lazyWithRetry(() => import('./pages/honeywell/honeywell-network')),
  },
  {
    path: '/agents/falcon-edge',
    element: lazyWithRetry(() => import('./pages/honeywell/honeywell-edge')),
  },
  {
    path: '/agents/raven',
    element: lazyWithRetry(() => import('./pages/tata')),
  },
  {
    path: '/agents/compressor',
    element: lazyWithRetry(() => import('./pages/demo/compressor')),
  },
  {
    path: '/agents/raven-2',
    element: lazyWithRetry(() => import('./pages/tata/use-case-2')),
  },
  {
    path: '/agents/wesco-dxa',
    element: lazyWithRetry(() => import('./pages/wesco')),
  },
  {
    path: '/agents/raven-3',
    element: lazyWithRetry(() => import('./pages/tata/use-case-3')),
  },
  {
    path: '/agents/:id',
    element: lazyWithRetry(() => import('./pages/train/agent-detail')),
  },
  {
    path: '/agents/:id/admin',
    element: lazyWithRetry(() => import('./pages/train/agent-admin')),
  },
  {
    path: '/agents/:id/:chat_id',
    element: lazyWithRetry(() => import('./pages/train/agent-detail')),
  },
];

const toolHubRoutes: RouteObject[] = [
  {
    path: '/tool-hub',
    element: lazyWithRetry(() => import('./pages/tool-hub')),
  },
  {
    path: '/tool-hub/:id',
    element: lazyWithRetry(() => import('./pages/tool-hub/tool-detail')),
  },
];

const dxaStoreRoutes: RouteObject[] = [
  {
    path: '/dxa-store',
    element: lazyWithRetry(() => import('./pages/dxa-store')),
  },
];

const adminRoutes: RouteObject[] = [
  {
    path: '/admin/settings',
    element: lazyWithRetry(() => import('./pages/admin/settings')),
  },
];

const profileRoutes: RouteObject[] = [
  {
    path: '/profile',
    element: lazyWithRetry(() => import('./pages/auth/profile')),
  },
];

const conversationRoutes: RouteObject[] = [
  {
    path: '/conversations',
    element: lazyWithRetry(() => import('./pages/conversations')),
  },
];

const superAdminRoutes: RouteObject[] = [
  {
    path: '/superadmin/workspace',
    element: lazyWithRetry(() => import('./pages/super-admin/workspace')),
  },
  {
    path: '/superadmin/llm',
    element: lazyWithRetry(() => import('./pages/super-admin/llm')),
  },
  {
    path: '/superadmin/workspace/:id',
    element: lazyWithRetry(() => import('./pages/super-admin/workspace-detail')),
  },
  {
    path: '/superadmin/agent/:id',
    element: lazyWithRetry(() => import('./pages/super-admin/agent-detail')),
  },
  {
    path: '/superadmin/users',
    element: lazyWithRetry(() => import('./pages/super-admin/users')),
  },
  {
    path: '/superadmin/users/:id',
    element: lazyWithRetry(() => import('./pages/super-admin/users-detail')),
  },
];

const errorRoutes: RouteObject[] = [
  { path: '/500', element: <GeneralError /> },
  { path: '/404', element: <NotFoundError /> },
  { path: '/503', element: <MaintenanceError /> },
  { path: '*', element: <NotFoundError /> },
];

const AppShellComponent = lazyWithRetry(() => import('./components/layout/app-shell'));

const router = createBrowserRouter([
  ...authRoutes,
  {
    path: '/superadmin',
    errorElement: <MaintenanceError />,
    children: [
      {
        element: (
          <ChunkErrorBoundary>
            <SuperAdminProtectRoute />
          </ChunkErrorBoundary>
        ),
        children: superAdminRoutes,
      },
    ],
  },
  {
    path: '/',
    element: AppShellComponent,
    errorElement: <MaintenanceError />,
    children: [
      ...sharingSessionRoutes,
      ...libraryRoutes,
      ...activeIssueRoutes,
      ...dxaFactoryRoutes,
      ...toolHubRoutes,
      ...dxaStoreRoutes,
      ...profileRoutes,
      ...conversationRoutes,
      {
        path: '/admin',
        element: (
          <ChunkErrorBoundary>
            <AdminProtectRoute />
          </ChunkErrorBoundary>
        ),
        children: adminRoutes,
      },
    ],
  },
  ...errorRoutes,
]);

export default router;
